import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { toast } from "sonner";
import { GraduationCap, Rocket, Save, RefreshCw, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";

const fmt = (v) => (v ? v.replace(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})$/, "$3/$2/$1 $4") : "");
const STATUS = {
  live: { label: () => "Live", cls: "bg-emerald-500 text-white", dot: "bg-emerald-400" },
  scheduled: { label: (c) => `Scheduled · from ${fmt(c.starts_at)}`, cls: "bg-sky-500 text-white", dot: "bg-sky-400" },
  expired: { label: (c) => `Expired · ${fmt(c.ends_at)}`, cls: "bg-amber-500 text-white", dot: "bg-amber-400" },
  disabled: { label: () => "Disabled", cls: "bg-muted text-muted-foreground", dot: "bg-gray-300" },
};

function ScheduleFields({ starts, ends, setStarts, setEnds }) {
  const field = (id, label, value, set) => (
    <div className="flex-1 min-w-[200px]">
      <label htmlFor={id} className="block text-xs font-semibold text-navy mb-1">{label}</label>
      <div className="flex gap-2">
        <Input id={id} type="datetime-local" value={value} onChange={(e) => set(e.target.value)} data-testid={id} className="h-10 rounded-xl" />
        {value && <Button type="button" variant="ghost" size="sm" onClick={() => set("")} data-testid={`${id}-clear`} className="rounded-full text-xs">Clear</Button>}
      </div>
    </div>
  );
  return (
    <div className="rounded-xl border border-border p-4" data-testid="banner-schedule">
      <div className="flex flex-wrap items-center gap-2 mb-3"><CalendarClock className="h-4 w-4 text-orange" /><span className="text-sm font-bold text-navy">Schedule</span><span className="text-xs text-muted-foreground">(Paris time · optional — empty = no limit · saved with “Save banner”)</span></div>
      <div className="flex flex-wrap gap-4">
        {field("banner-starts-at", "Start", starts, setStarts)}
        {field("banner-ends-at", "End", ends, setEnds)}
      </div>
      {starts && ends && ends <= starts && <p className="text-xs text-red-600 mt-2" data-testid="banner-schedule-error">End must be after start.</p>}
    </div>
  );
}

export function BannersTab() {
  const [persona, setPersona] = useState("undergraduate");
  const [data, setData] = useState(null);
  const [html, setHtml] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");

  const reload = useCallback(() => {
    api.get("/admin/banners").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load banners"));
  }, []);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (data) setHtml(data[persona]?.html || "");
  }, [data?.[persona]?.html, persona]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data) setEnabled(!!data[persona]?.enabled);
  }, [data, persona]);
  useEffect(() => {
    if (data) { setStarts(data[persona]?.starts_at || ""); setEnds(data[persona]?.ends_at || ""); }
  }, [data?.[persona]?.starts_at, data?.[persona]?.ends_at, persona]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLive = async (v) => {
    setEnabled(v);
    try {
      const { data: r } = await api.patch("/admin/banners/enabled", { persona, enabled: v });
      toast.success(`Banner ${v ? "enabled" : "disabled"} for ${persona}`);
      setData((d) => ({ ...d, [persona]: { ...d[persona], enabled: v, status: r.status } }));
    } catch (err) {
      setEnabled(!v);
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/banners", { persona, html, enabled, starts_at: starts || null, ends_at: ends || null });
      toast.success(`Banner saved for ${persona}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const cur = data?.[persona] || {};
  const st = STATUS[cur.status || "disabled"];

  if (!data) return <div className="py-16 text-center text-muted-foreground">Loading banners…</div>;

  return (
    <div className="space-y-5" data-testid="admin-banners">
      <Card className="p-4 rounded-2xl border-border bg-navy/5">
        <p className="text-sm text-navy">Publish a notification banner shown at the top of every page for logged-in students of the selected level. Use the toolbar to add formatting and <b>links</b>. Use the “Banner enabled” checkbox to show or hide it instantly.</p>
      </Card>

      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-navy">Student type:</span>
        {[{ k: "undergraduate", i: GraduationCap, l: "Undergraduate" }, { k: "postgraduate", i: Rocket, l: "Postgraduate" }].map((p) => {
          const Icon = p.i; const active = persona === p.k;
          return (
            <button key={p.k} type="button" data-testid={`banner-persona-${p.k}`} onClick={() => setPersona(p.k)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-orange text-white" : "bg-white border border-border text-navy hover:border-navy/30"}`}>
              <Icon className="h-4 w-4" /> {p.l}
              <span data-testid={`banner-dot-${p.k}`} className={`h-2 w-2 rounded-full ${STATUS[data[p.k]?.status || "disabled"].dot}`} title={STATUS[data[p.k]?.status || "disabled"].label(data[p.k] || {})} />
            </button>
          );
        })}
      </div>

      <Card className="p-6 rounded-2xl border-border space-y-4">
        <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${enabled ? "border-emerald-300 bg-emerald-50" : "border-border bg-secondary/40"}`} data-testid="banner-enabled-row">
          <Checkbox checked={enabled} onCheckedChange={(v) => toggleLive(!!v)} data-testid="banner-enabled-checkbox" className="h-5 w-5" />
          <span className="flex-1">
            <span className="block text-sm font-bold text-navy">Banner enabled</span>
            <span className="block text-xs text-muted-foreground">Applied instantly to {persona} students — no need to save.</span>
          </span>
          <span data-testid="banner-status" className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${st.cls}`}>{st.label(cur)}</span>
        </label>
        <ScheduleFields starts={starts} ends={ends} setStarts={setStarts} setEnds={setEnds} />
        <RichTextEditor value={html} onChange={setHtml} />

        <div className="rounded-xl border border-dashed border-border p-4">
          <div className="eyebrow text-[11px] text-orange mb-2">Live preview</div>
          {html?.trim() ? (
            <div className="bg-orange/10 border border-orange/30 rounded-xl px-4 py-3 text-sm text-navy [&_a]:text-orange [&_a]:underline [&_a]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" data-testid="banner-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] }) }} />
          ) : <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs text-muted-foreground">Saving updates the content and schedule; the checkbox above switches it on/off instantly.</span>
          <Button data-testid="banner-save" onClick={save} disabled={saving || (starts && ends && ends <= starts)} className="rounded-full bg-orange hover:bg-orange-dark text-white">
            {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save banner</>}
          </Button>
        </div>
      </Card>
    </div>
  );
}
